<template>
    <div v-if="editor" class="mt-2">
        <div v-if="editorConfigs?.isHtmlButton" class="flex justify-end gap-2">
            <hrm-button v-if="isHtmlButton" @click="getHtmlEditor" buttonMode="iconTextButton" iconSize="14px"
                buttonPadding="2px 10px" buttonSize="28px"
                buttonClass="hrm-tip-tap-button flex justify-center items-center rounded-sm" buttonAlign="center"
                icon="ooui:bold-b" title="Save HTML" tooltipPosition="top" />
        </div>
        <div v-if="editorConfigs?.textLimit?.display" class="flex items-center justify-end gap-2 mb-2" :class="{
            'character-count': true,
            'character-count--warning': editor.storage.characterCount.characters() >= editorConfigs?.textLimit?.limit,
        }">
            <svg height="20" width="20" viewBox="0 0 20 20">
                <circle r="10" cx="10" cy="10" fill="#e9ecef" />
                <circle r="5" cx="10" cy="10" fill="transparent" stroke="currentColor" stroke-width="10"
                    :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`"
                    transform="rotate(-90) translate(-20)" />
                <circle class="word-count-bg" r="6" cx="10" cy="10" />
            </svg>

            {{ editor.storage.characterCount.characters() }} / {{ editorConfigs?.textLimit?.limit }} characters

            {{ editor.storage.characterCount.words() }} words
        </div>
        <div class="hrm-tip-editor-groups w-full">
            <template v-if="editorConfigs.editorType === 'small' && !isExpanded">
                <div @click="isExpanded = true" class="px-4 py-2 rounded-sm">
                    <div class="collapsed-placeholder">
                        Write an update and mention others with @
                    </div>
                    <div class="flex justify-end gap-1 mt-4" @click.stop>
                        <hrm-button @click="openAdd()" buttonMode="iconButton" iconSize="15px" buttonPadding="0"
                            buttonSize="28px" buttonClass="flex justify-center items-center" buttonAlign="center"
                            icon="fluent:mention-32-filled" tooltipText="Add Mentions" tooltipPosition="top" />

                        <hrm-button @file="onHandleInputFile" buttonMode="fileUploadButton" iconSize="15px"
                            buttonPadding="0" buttonSize="28px" buttonClass="flex justify-center items-center"
                            buttonAlign="center" icon="tdesign:attach" tooltipText="Add File" tooltipPosition="top" />

                        <hrm-button @click="openAdd()" buttonMode="emojiButton" iconSize="18px" buttonPadding="0"
                            buttonSize="28px" buttonClass="flex justify-center items-center text-sm"
                            buttonAlign="center" icon="😀" tooltipText="Add Emoji" tooltipPosition="top" />
                    </div>
                </div>
            </template>
            <template v-else>
                <!-- Controls Components -->
                <HiddenControls />
                <!-- Editor Components -->
                <drag-handle v-if="editorConfigs?.isDragDrop" class="tip-tap-drag" :editor="editor">
                    <div class="tip-tap-drag-handle" />
                </drag-handle>
                <div v-if="editor" class="flex tip-tap-editor-main">
                    <editor-content class="tip-tap-wrapper" :class="[
                        editorConfigs?.isTableContent ? 'tip-tap-have-table-content' : 'w-full',
                        editorConfigs?.isDragDrop ? 'tip-tap-have-drag-drop' : 'tip-tap-common'
                    ]" :editor="editor" />

                    <div v-if="editorConfigs?.isTableContent" class="tip-tap-table-content-sidebar">
                        <div class="sidebar-options">
                            <div class="label-large">Table of contents</div>
                            <div class="table-of-contents">
                                <ToC />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import axios from 'axios'
import { onMounted, onUnmounted, ref, defineAsyncComponent, provide, Ref, inject, computed } from 'vue'
import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { convertJsonToFormData } from '@/helpers'

// Types for Editors
import type { EditorConfig, ToolbarOption } from './Helpers/types'
import { EditorConfigsKey } from './Helpers/keys'
// Import Data from editorData file
import { editorProps, defaultOptions, filterCommands } from './Helpers/editorData'

// Extensions
import Placeholder from '@tiptap/extension-placeholder'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Color from '@tiptap/extension-color'
import Image from '@tiptap/extension-image'
import { TextStyle } from '@tiptap/extension-text-style'
import { all, createLowlight } from 'lowlight'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { CharacterCount } from '@tiptap/extensions'

import { getHierarchicalIndexes, TableOfContents } from '@tiptap/extension-table-of-contents'
import { Mathematics, migrateMathStrings } from '@tiptap/extension-mathematics'

// import DragHandle from '@tiptap/extension-drag-handle';
import { DragHandle } from '@tiptap/extension-drag-handle-vue-3'
import NodeRange from '@tiptap/extension-node-range'

// Table Import
import { Table, TableKit } from "@tiptap/extension-table"
// import { TableRow } from "@tiptap/extension-table-row"
// import { TableCell } from "@tiptap/extension-table-cell"
// import { TableHeader } from "@tiptap/extension-table-header"

// 
import Superscript from '@tiptap/extension-superscript'
import Subscript from '@tiptap/extension-subscript'
import Youtube from '@tiptap/extension-youtube'

// Highlight.js langs
import css from 'highlight.js/lib/languages/css'
import js from 'highlight.js/lib/languages/javascript'
import ts from 'highlight.js/lib/languages/typescript'
import html from 'highlight.js/lib/languages/xml'

// Css Files
import 'tippy.js/dist/tippy.css'
import '../../../css/Editors/tiptapEditor.css';
import '../../../css/Editors/math.css';

//Mentions or Suggestions
import {
    SlashCommand, MentionCommand, Mention, CustomImage,
    EmojiSuggestion, UploadImageOnPaste, FontSize, FontFamily, AiGenerateText, AiNode,
    CustomTableCell, CustomTable
} from '@/Common/TextEditors/Helpers/tiptapMention';

// Controls
import HiddenControls from './Comp/HiddenControls.vue';
// Lowlight instance
const lowlight = createLowlight(all)
lowlight.register('html', html)
lowlight.register('css', css)
lowlight.register('js', js)
lowlight.register('ts', ts)

// Props & emit
const props = defineProps(['modelValue']);
const emit = defineEmits(['update:modelValue', 'showHtml', 'handlePaste', 'imageUploadData', 'isUploading', 'uploadResponse', 'onUpdate', 'onBlur'])

// Inject with full typing
const editorConfigs = inject(
    EditorConfigsKey,
    ref<EditorConfig>(editorProps)
);

// Refs
const editor = ref(null);
const defaultToolbarOptions = ref<ToolbarOption[]>([])
const tableContentItems = ref([])
const isExpanded = ref<boolean>(false);


// Lazy Loading components
const ToC = editorConfigs?.value?.isTableContent
    ? defineAsyncComponent(() => import("./Comp/TableContentExtenstion/ToC.vue"))
    : null

// HTML output
const getHtmlEditor = () => {
    const html = editor.value.getHTML()
    emit('showHtml', html)
}

// update text html in tiptap 
const updateEditorData = (newContent: string, isHTML = true) => {
    if (!editor.value) return;

    const currentEditor = editor.value;

    currentEditor.chain().focus();

    // Use transaction for cursor-safe insertion
    const { from, to } = currentEditor.state.selection;

    currentEditor
        .chain()
        .focus()
        .insertContentAt(
            { from, to },
            isHTML ? newContent : { type: 'text', text: newContent }
        )
        .run();
};


// const handleDrop = async (view, event) => {
//     const hasFiles = !!event.dataTransfer?.files?.length
//     if (!hasFiles) return false

//     const images = Array.from(event.dataTransfer.files).filter(f =>
//         f.type.startsWith('image/'),
//     )
//     if (images.length === 0) return false

//     event.preventDefault()
//     for (const img of images) {
//         const url = await handleImageUpload(img)
//         if (url) {
//             editor.value.chain().focus().setImage({ src: url }).run()
//         }
//     }
//     return true
// }

// const 
const percentage = computed(() => {
    return Math.round((100 / editorConfigs?.value?.textLimit?.limit) * editor.value?.storage?.characterCount?.characters())
})

const onHandleInputFile = (e: any) => {
    const imageItems = e.target.files;
    emit("handlePaste", imageItems);
}

/* ---------------------------------
   INIT EDITOR
--------------------------------- */
onMounted(() => {
    // Setting Toolbar options
    if (editorConfigs?.value?.toolbarType === "Custom") {
        defaultToolbarOptions.value = editorConfigs?.value?.toolbarOptions;
    } else if (editorConfigs?.value?.toolbarType === "Essential") {
        defaultToolbarOptions.value = ["bold",
            "italic",
            "strike",
            "paragraph",
            "heading",
            "list",
            "link", "code", "codeBlock", "highlight", "fontColor", "fontFamily", "fontSize", "clearFormat", "signAdd"];
    } else {
        defaultToolbarOptions.value = defaultOptions;
    }

    editor.value = new Editor({
        extensions: [
            StarterKit.configure({
                codeBlock: false,
            }),
            TextStyle,
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('subScript') ? [
                Subscript] : []),

            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('supScript') ? [
                Superscript] : []),

            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('math') ? [
                Mathematics.configure({
                    katexOptions: {
                        throwOnError: false,
                    },
                }),
            ] : []),

            // Add Extensions conditionally based on toolbar options
            // Font Color Extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('fontColor') ? [
                Color.configure({
                    types: ['textStyle', 'listItem', 'heading', 'paragraph'],
                }),
            ] : []),

            // Text Align Extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('align') ? [
                TextAlign.configure({ types: ['heading', 'paragraph'] }),
            ] : []),

            // Task List Extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('list') ? [
                TaskList,
                TaskItem.configure({ nested: true }),
            ] : []),

            // Highlight Color Extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('highlight') ? [
                Highlight.configure({ multicolor: true }),
            ] : []),

            ...((defaultToolbarOptions.value && defaultToolbarOptions.value.includes('youtube')) ? [
                Youtube.configure({
                    controls: true,
                    nocookie: true,
                    HTMLAttributes: {
                        class: 'rounded-md',
                    },
                }),
            ] : []),

            // Inline Image Base64
            Image.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: { class: 'my-custom-class' },
            }),

            ...(editorConfigs?.value?.textLimit?.display ? [CharacterCount.configure({
                limit: editorConfigs?.value?.textLimit?.display ? editorConfigs?.value?.textLimit?.limit : 10000,
            }),] : []),

            // Codeblock highlighter extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('codeBlock') ? [
                CodeBlockLowlight.configure({
                    lowlight,
                    tabSize: 2,
                }),
            ] : []),

            // Placeholder For Editor Extension
            Placeholder.configure({
                placeholder: ({ node, editor }) => {
                    const firstNode = editor.state.doc.firstChild
                    if (node === firstNode && node.content.size === 0) return editorConfigs?.value?.placeholder

                    if (node.type.name === 'paragraph' && node.content.size === 0) {
                        const selFrom = editor.state.selection.from
                        // Only resolve before if the node has a parent
                        const resolved = editor.state.doc.resolve(selFrom)
                        const nodeStart = resolved.depth > 0 ? resolved.before() : 0
                        if ((selFrom >= nodeStart && selFrom <= nodeStart + node.nodeSize) && editorConfigs?.value?.isSlashCommand) {
                            return "Type '/' to open commands…"
                        }
                    }

                    return ''
                },
                showOnlyWhenEditable: true,
                emptyEditorClass: 'is-tip-tap-editor-empty',
            }),


            ...(editorConfigs?.value?.isTableContent ? [
                TableOfContents.configure({
                    getIndex: getHierarchicalIndexes,
                    scrollParent: () => editor.value.view.dom,
                    onUpdate: content => {
                        tableContentItems.value = content.map(item => ({
                            id: item.id,
                            level: item.level,
                            textContent: item.textContent,
                            isActive: item.isActive,
                            isScrolledOver: item.isScrolledOver,
                            itemIndex: item.itemIndex,
                        }))
                    },
                }),
            ] : []),

            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('table') ? [
                // ✅ do NOT configure resizable here

                TableKit.configure({
                    table: {
                        resizable: true,
                        HTMLAttributes: {
                            style: 'width:100%; border-collapse: collapse; table-layout: fixed;',
                        },  // ✅ only here
                    },
                    tableCell: false,
                }),
                //  CustomTable,

                CustomTableCell,
            ] : []),

            // Drag Drop Nodes
            ...(editorConfigs?.value?.isDragDrop ? [NodeRange.configure({
                depth: 0,
                key: null,
            }),] : []),


            // 🔥 Slash command extension
            ...(editorConfigs?.value?.isSlashCommand ? [SlashCommand(filterCommands(defaultToolbarOptions.value))] : []),
            // 🔥 Mention command extension
            ...(editorConfigs?.value?.isMention ? [MentionCommand(editorConfigs?.value?.mentionList), Mention] : []),
            // 🔥 Image extension
            // ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes("image") ? [CustomImage] : []),
            // 🔥 Eomji extension
            ...(editorConfigs?.value?.isEmojiPicker ? [EmojiSuggestion] : []),
            // Image Paste Extension
            UploadImageOnPaste.configure({
                pasteImageType: editorConfigs?.value?.pasteImageType,
                uploadFn: async (files) => {
                    try {
                        emit("isUploading", true);
                        const formImageData = editorConfigs?.value?.imageUploadData?.params
                            ? { ...editorConfigs?.value?.imageUploadData?.params }
                            : {};

                        const formData = convertJsonToFormData(formImageData);

                        files.forEach((file, i) => {
                            formData.append(
                                `${editorConfigs?.value?.imageUploadData?.dataKey}[${i}]` || `image[${i}]`,
                                file
                            );
                        });

                        const config = {
                            headers: { "Content-Type": "multipart/form-data" },
                        };

                        const response = await axios[editorConfigs?.value?.imageUploadData?.method || "post"](
                            editorConfigs?.value?.imageUploadData?.route || "/api/upload",
                            formData,
                            config
                        );

                        // Emit to parent
                        emit("uploadResponse", response);
                        emit("isUploading", false);

                        // Return an array of uploaded file URLs
                        return {
                            images: response.data?.data,
                            imageKey: editorConfigs?.value?.imageUploadData?.imageUrlKey || "urls"
                        };
                    } catch (error) {
                        console.error("Image upload failed:", error);
                        throw error;
                    }
                },
            }),
            // Font Size Extension
            ...(defaultToolbarOptions.value && defaultToolbarOptions.value.includes('fontSize') ? [
                FontSize] : []),
            // Font Family Extension
            ...(defaultToolbarOptions.value && (defaultToolbarOptions.value.includes('fontFamily')) ? [
                FontFamily] : []),
            // AI Generate Text Extension
            ...(defaultToolbarOptions.value && (defaultToolbarOptions.value.includes('aiGenerate')) ? [
                AiGenerateText] : []),
        ],
        content: props.modelValue,
        // On Update V-model and emit to parent Component
        onUpdate: ({ editor }) => {
            emit('update:modelValue', editor.getHTML())
            emit('onUpdate', editor.getHTML())
        },
        onBlur({ event, editor }) {
            emit('onBlur', editor.getHTML())
        },
        onCreate: ({ editor: currentEditor }) => {
            migrateMathStrings(currentEditor)
        },

        editable: !editorConfigs?.value?.isDisabled,
    })

    // On Paste anything in the editor

    editor.value?.on("paste", ({ event }) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (editorConfigs?.value?.pasteImageType === "pasteImage") {
            const items = event.clipboardData?.items;
            if (items && items.length > 0) {
                event.preventDefault();
                // Filter only image items
                const imageItems = Array.from(items as DataTransferItem[]).filter((item: DataTransferItem) =>
                    item.type.startsWith("image/")
                );
                if (imageItems.length > 0) {
                    // Emit to parent
                    emit("handlePaste", imageItems);
                    // Prevent default paste behavior
                    return true;
                }
            }
        }
    });
})
onUnmounted(() => {
    if (editor.value) {
        editor.value.destroy()
        editor.value = null
    }
})

// Provide Tiptap Editor Nested Childs
provide("tiptapEditor", editor);
provide("toolbarOptions", defaultToolbarOptions)
provide("tableContentItems", tableContentItems);

defineExpose({
    updateEditorData
})
</script>

<style scoped>
    .tip-tap-editor-main{
        overflow-y: auto;
        max-height: 600px;
    }
</style>